import React, { useState, useEffect, useCallback } from "react";
import { JsonRpc } from "@proton/js";
import { normalizeTimestamp, formatDate } from "../utils/dateUtils";
import Loader from "./loader/Loader";
import {
  BadgeCheck,
  BadgeDollarSign,
  CircleCheckBig,
  Clock3,
  Info,
  List,
  TriangleAlert,
} from "lucide-react";
import { TRADING_TOKEN } from "../configs/config";

interface Market {
  id: number;
  question: string;
  category: string;
  expire: number;
  expireSec: number;
  resolved: boolean;
  outcome: number;
  image_url?: string;
  outcomes_count: number;
  outcomes?: Outcome[];
  version?: number;
  q_yes?: number;
  q_no?: number;
  b?: number;
  total_collateral_in?: number;
  resolved_at?: number;
}

interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
}

interface ResProposal {
  proposal_id: number;
  market_id: number;
  proposer: string;
  created_at: number;
  voting_end_at: number;
  is_resolved: boolean;
  winning_outcome_id: number;
  decided_by_admin: boolean;
  paused: boolean;
}

interface ResConfig {
  next_proposal_id: number;
  resolution_expiry_sec: number;
  eligible_voters_count: number;
  winning_threshold: number;
}

interface MarketWithProposal extends Market {
  proposal?: ResProposal;
  isEligibleVoter?: boolean;
  totalVotes?: number;
  outcomeVoteCounts?: Record<number, number>;
}

interface MarketResolutionProps {
  session: any;
  onSelectMarket: (
    marketId: number,
    tab: "open" | "voting" | "stalled" | "closed" | "illiquid",
  ) => void;
}

const MarketResolution: React.FC<MarketResolutionProps> = ({
  session,
  onSelectMarket,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<
    "open" | "voting" | "stalled" | "closed" | "illiquid"
  >("open");
  const [marketsWithProposals, setMarketsWithProposals] = useState<
    MarketWithProposal[]
  >([]);
  const [allMarkets, setAllMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState<Record<number, string>>({});
  const [resConfig, setResConfig] = useState<ResConfig | null>(null);

  const fetchMarketsAndProposals = useCallback(async () => {
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const proposalsResult = await rpc.get_table_rows({
        code: contractName,
        scope: contractName,
        table: "proposals",
        limit: 200,
        reverse: true,
      });

      let fetchedConfig: ResConfig | null = null;
      try {
        const configResult = await rpc.get_table_rows({
          code: contractName,
          scope: contractName,
          table: "resconfig",
          limit: 1,
        });
        if (configResult.rows.length > 0) {
          const c = configResult.rows[0];
          fetchedConfig = {
            next_proposal_id: c.next_proposal_id || 1,
            resolution_expiry_sec: c.resolution_expiry_sec || 172800,
            eligible_voters_count: c.eligible_voters_count || 21,
            winning_threshold: c.winning_threshold || 5,
          };
          setResConfig(fetchedConfig);
        }
      } catch (error) {
        console.error("Error fetching resolution config:", error);
      }

      const proposals: ResProposal[] = proposalsResult.rows.map((row: any) => ({
        proposal_id: row.proposal_id,
        market_id: row.market_id,
        proposer: row.proposer,
        created_at: normalizeTimestamp(row.created_at),
        voting_end_at: normalizeTimestamp(row.voting_end_at),
        is_resolved: !!row.is_resolved,
        winning_outcome_id:
          row.winning_outcome_id != null ? row.winning_outcome_id : 255,
        decided_by_admin: !!row.decided_by_admin,
        paused: !!row.paused,
      }));

      const marketsResult = await rpc.get_table_rows({
        code: contractName,
        scope: contractName,
        table: "markets3",
        lower_bound: "0",
        limit: 200,
        reverse: true,
      });

      const marketsMap = new Map<number, Market>();
      const fetchedMarkets: Market[] = [];
      for (const row of marketsResult.rows) {
        const market: Market = {
          ...row,
          category: row.category || "General",
          expireSec: normalizeTimestamp(row.expire),
          outcomes_count: row.outcomes_count || 2,
          resolved: !!row.resolved,
          version: row.version || 0,
          q_yes: row.q_yes || 0,
          q_no: row.q_no || 0,
          b: row.b || 500_000_000,
          total_collateral_in: row.total_collateral_in || 0,
        };

        if (market.outcomes_count > 0) {
          try {
            const outcomesResult = await rpc.get_table_rows({
              code: contractName,
              scope: row.id.toString(),
              table: "outcomes",
              limit: 100,
            });

            const outcomes: Outcome[] = outcomesResult.rows.map(
              (outcomeRow: any) => ({
                outcome_id: outcomeRow.outcome_id,
                name: outcomeRow.name,
                display_order: outcomeRow.display_order,
              }),
            );

            outcomes.sort((a, b) => a.display_order - b.display_order);
            market.outcomes = outcomes;
          } catch (error) {
            console.error(
              `Error fetching outcomes for market ${row.id}:`,
              error,
            );
            market.outcomes = [];
          }
        }

        marketsMap.set(market.id, market);
        fetchedMarkets.push(market);
      }

      setAllMarkets(fetchedMarkets);

      const currentUser = session?.auth?.actor?.toString();
      const marketsWithProposalData: MarketWithProposal[] = [];

      let userVoterSnap: any = null;
      if (currentUser) {
        try {
          const voterSnapResult = await rpc.get_table_rows({
            code: contractName,
            scope: contractName,
            table: "votersnaps",
            lower_bound: currentUser,
            upper_bound: currentUser,
            limit: 1,
          });
          if (
            voterSnapResult.rows.length > 0 &&
            voterSnapResult.rows[0].account === currentUser
          ) {
            userVoterSnap = voterSnapResult.rows[0];
          }
        } catch (error) {
          console.error("Error fetching user voter snap:", error);
        }
      }

      for (const proposal of proposals) {
        const market = marketsMap.get(proposal.market_id);
        if (market) {
          let isEligibleVoter = false;

          if (userVoterSnap) {
            const userProposals = userVoterSnap.proposals || [];
            const eligibility = userProposals.find(
              (p: any) => p.proposal_id === proposal.proposal_id,
            );
            if (eligibility) {
              isEligibleVoter = true;
            }
          }

          let totalVotes = 0;
          const outcomeVoteCounts: Record<number, number> = {};
          try {
            const votesResult = await rpc.get_table_rows({
              code: contractName,
              scope: proposal.proposal_id.toString(),
              table: "propvotes",
              limit: 200,
            });
            totalVotes = votesResult.rows.length;
            for (const vote of votesResult.rows) {
              const oid = vote.chosen_outcome;
              outcomeVoteCounts[oid] = (outcomeVoteCounts[oid] || 0) + 1;
            }
          } catch (error) {
            console.error(
              `Error fetching votes for proposal ${proposal.proposal_id}:`,
              error,
            );
          }

          marketsWithProposalData.push({
            ...market,
            proposal,
            isEligibleVoter,
            totalVotes,
            outcomeVoteCounts,
          });
        }
      }

      setMarketsWithProposals(marketsWithProposalData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching markets and proposals:", error);
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchMarketsAndProposals();
    const interval = setInterval(fetchMarketsAndProposals, 10000);
    return () => clearInterval(interval);
  }, [fetchMarketsAndProposals]);

  useEffect(() => {
    const updateCountdowns = () => {
      const now = Math.floor(Date.now() / 1000);
      const newCountdowns: Record<number, string> = {};

      marketsWithProposals.forEach((market) => {
        if (market.proposal) {
          const votingRemaining = market.proposal.voting_end_at - now;
          if (votingRemaining <= 0) {
            newCountdowns[market.id] = "Ended";
          } else {
            const hours = Math.floor(votingRemaining / 3600);
            const minutes = Math.floor((votingRemaining % 3600) / 60);
            const secs = votingRemaining % 60;
            newCountdowns[market.id] =
              hours + "h " + minutes + "m " + secs + "s";
          }
        }
      });

      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [marketsWithProposals]);

  const now = Math.floor(Date.now() / 1000);

  // Helper: detect illiquid markets (expired, not resolved, no trading or one-sided trading)
  const isIlliquid = (market: Market): boolean => {
    if (market.resolved) return false;
    if (market.expireSec > now) return false;
    const totalIn = market.total_collateral_in || 0;
    const qYes = market.q_yes || 0;
    const qNo = market.q_no || 0;
    // No trading: no collateral entered at all
    if (totalIn === 0) return true;
    // One-sided trading (binary): only one side has shares
    if ((market.outcomes_count || 2) === 2 && (qYes === 0 || qNo === 0))
      return true;
    return false;
  };

  const getIlliquidType = (market: Market): "no_trading" | "one_sided" => {
    const totalIn = market.total_collateral_in || 0;
    if (totalIn === 0) return "no_trading";
    return "one_sided";
  };

  const illiquidMarkets = allMarkets.filter((market) => {
    if (market.resolved) return false;
    if (market.expireSec > now) return false;
    return isIlliquid(market);
  });

  const openMarkets = allMarkets.filter((market) => {
    if (market.resolved) return false;
    if (market.expireSec > now) return false;
    if (isIlliquid(market)) return false;
    const hasProposal = marketsWithProposals.some((m) => m.id === market.id);
    return !hasProposal;
  });

  const votingMarkets = marketsWithProposals.filter((market) => {
    if (!market.proposal) return false;
    if (market.proposal.is_resolved) return false;
    if (market.proposal.paused) return false;
    return now < market.proposal.voting_end_at;
  });

  const stalledMarkets = marketsWithProposals.filter((market) => {
    if (!market.proposal) return false;
    if (market.proposal.is_resolved) return false;
    if (now < market.proposal.voting_end_at) return false;
    return true;
    // const threshold = resConfig?.winning_threshold || 5;
    // const counts = market.outcomeVoteCounts || {};
    // const anyOutcomePassed = Object.values(counts).some((c) => c >= threshold);
    // return !anyOutcomePassed;
  });
  const closedMarkets = marketsWithProposals.filter((market) => {
    if (!market.proposal) return false;
    return market.proposal.is_resolved;
  });

  const formatVolume = (market: Market | MarketWithProposal) => {
    const volume = Math.floor((market.total_collateral_in || 0) / 1_000_000);
    return volume + " " + TRADING_TOKEN;
  };

  const metaIconProps = {
    size: 14,
    strokeWidth: 2,
  };

  const outcomeIconProps = {
    size: 16,
    strokeWidth: 2,
  };

  if (loading) {
    return <Loader label="Loading markets..." />;
  }

  return (
    <div className="market-resolution">
      <div className="resolution-header">
        <h2>Market Resolution</h2>
        <p className="resolution-description">
          Track and participate in the resolution lifecycle of prediction
          markets.
        </p>
      </div>

      <div className="resolution-sub-tabs">
        <button
          className={activeSubTab === "open" ? "active" : ""}
          onClick={() => setActiveSubTab("open")}
        >
          Open ({openMarkets.length})
        </button>
        <button
          className={activeSubTab === "voting" ? "active" : ""}
          onClick={() => setActiveSubTab("voting")}
        >
          Voting ({votingMarkets.length})
        </button>
        <button
          className={activeSubTab === "stalled" ? "active" : ""}
          onClick={() => setActiveSubTab("stalled")}
        >
          Stalled ({stalledMarkets.length})
        </button>
        <button
          className={activeSubTab === "closed" ? "active" : ""}
          onClick={() => setActiveSubTab("closed")}
        >
          Closed ({closedMarkets.length})
        </button>
        <button
          className={activeSubTab === "illiquid" ? "active" : ""}
          onClick={() => setActiveSubTab("illiquid")}
          style={{
            backgroundColor:
              activeSubTab === "illiquid" ? "#f59e0b" : undefined,
          }}
        >
          Illiquid ({illiquidMarkets.length})
        </button>
      </div>

      <div className="resolution-content">
        {activeSubTab === "open" && (
          <>
            <h3 className="tab-page-title">Unresolved Markets</h3>
            <div className="resolution-info-banner">
              <Info size={18} />
              <span>
                Expired markets awaiting resolution. Select a market to create a
                resolution proposal.
              </span>
            </div>

            {openMarkets.length === 0 ? (
              <div className="no-markets-message">
                <p>No unresolved markets found.</p>
              </div>
            ) : (
              <div className="resolution-markets-list">
                {openMarkets.map((market) => (
                  <div
                    key={market.id}
                    className="resolution-market-list-item"
                    onClick={() => onSelectMarket(market.id, "open")}
                  >
                    <div className="list-item-main">
                      <div className="list-item-header">
                        <span className="market-type-badge">
                          {market.category}
                        </span>
                        <span className="status-badge expired">Expired</span>
                      </div>
                      <h3 className="list-item-question">{market.question}</h3>
                      <div className="list-item-meta">
                        <span className="meta-item">
                          <BadgeDollarSign {...metaIconProps} />
                          {formatVolume(market)}
                        </span>
                        <span className="meta-item">
                          <Clock3 {...metaIconProps} />
                          Expired: {formatDate(market.expireSec)}
                        </span>
                        <span className="meta-item">
                          <List {...metaIconProps} />
                          {market.outcomes_count} outcomes
                        </span>
                      </div>
                      {market.outcomes && market.outcomes.length > 0 && (
                        <div className="resolution-outcomes-preview">
                          {market.outcomes.slice(0, 4).map((o) => (
                            <span key={o.outcome_id} className="outcome-tag">
                              {o.name}
                            </span>
                          ))}
                          {market.outcomes.length > 4 && (
                            <span className="outcome-tag more">
                              +{market.outcomes.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeSubTab === "voting" && (
          <>
            <h3 className="tab-page-title">Proposed Resolutions</h3>
            <div className="resolution-info-banner">
             <Info size={18} />
              <span>
                Markets with active resolution proposals. Eligible voters can
                vote on the proposed outcome.
                {session
                  ? votingMarkets.some((m) => m.isEligibleVoter)
                    ? " You are eligible to vote on some proposals."
                    : " You are not in the eligible voters list for these proposals."
                  : " Connect your wallet to participate."}
              </span>
            </div>

            <div className="resolution-eligibility-note">
              <strong>Note:</strong> Only the top 21 XPRED holders are eligible
              to vote on the market resolution.
            </div>

            {votingMarkets.length === 0 ? (
              <div className="no-markets-message">
                <p>No proposals are currently in the voting phase.</p>
              </div>
            ) : (
              <div className="resolution-markets-list">
                {votingMarkets.map((market) => {
                  const proposal = market.proposal!;
                  const countdown = countdowns[market.id] || "Calculating...";

                  return (
                    <div
                      key={market.id}
                      className="resolution-market-list-item"
                      onClick={() => onSelectMarket(market.id, "voting")}
                    >
                      <div className="list-item-main">
                        <div className="list-item-header">
                          <span className="market-type-badge">
                            {market.category}
                          </span>
                          {market.isEligibleVoter && (
                            <span className="eligible-badge">
                              Eligible Voter
                            </span>
                          )}
                          <span className="status-badge voting">
                            Voting Active
                          </span>
                        </div>
                        <h3 className="list-item-question">
                          {market.question}
                        </h3>
                        <div className="list-item-meta">
                          <span className="meta-item">
                            <BadgeCheck {...metaIconProps} />
                            Proposal #{proposal.proposal_id}
                          </span>
                          <span className="meta-item">
                            <BadgeDollarSign {...metaIconProps} />
                            {formatVolume(market)}
                          </span>
                          <span className="meta-item">
                            <Clock3 {...metaIconProps} />
                            Expired: {formatDate(market.expireSec)}
                          </span>
                        </div>
                        <div className="resolution-proposal-info">
                          <div className="proposal-proposer">
                            Proposed by: {proposal.proposer}
                          </div>
                        </div>
                        <div className="voting-window-remaining">
                          <span>Voting Window Remaining: {countdown}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeSubTab === "stalled" && (
          <>
            <h3 className="tab-page-title">Stalled Proposals</h3>
            <div className="resolution-info-banner warning">
             <TriangleAlert size={18} />
              <span>
                These proposals have exceeded their voting period. Admin action is now required to approve this proposal.
              </span>
            </div>

            {stalledMarkets.length === 0 ? (
              <div className="no-markets-message">
                <p>No stalled proposals found.</p>
              </div>
            ) : (
              <div className="resolution-markets-list">
                {stalledMarkets.map((market) => {
                  const proposal = market.proposal!;

                  return (
                    <div
                      key={market.id}
                      className="resolution-market-list-item"
                      onClick={() => onSelectMarket(market.id, "stalled")}
                    >
                      <div className="list-item-main">
                        <div className="list-item-header">
                          <span className="market-type-badge">
                            {market.category}
                          </span>
                          <span className="status-badge stalled">Stalled</span>
                        </div>
                        <h3 className="list-item-question">
                          {market.question}
                        </h3>
                        <div className="list-item-meta">
                          <span className="meta-item">
                            <BadgeCheck {...metaIconProps} />
                            Proposal #{proposal.proposal_id}
                          </span>
                          <span className="meta-item">
                            <BadgeDollarSign {...metaIconProps} />
                            {formatVolume(market)}
                          </span>
                          <span className="meta-item">
                            <Clock3 {...metaIconProps} />
                            Expired: {formatDate(market.expireSec)}
                          </span>
                        </div>
                        <div className="resolution-proposal-info">
                          <div className="proposal-proposer">
                            Proposed by: {proposal.proposer}
                          </div>
                        </div>
                        <div className="voting-window-remaining ended">
                          <span>
                            Voting period ended &middot; Awaiting admin
                            resolution
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeSubTab === "closed" && (
          <>
            <h3 className="tab-page-title">Resolved Proposals</h3>
            <div className="resolution-info-banner success">
             <CircleCheckBig size={18} />
              <span>
                These resolution proposals have been resolved, either by reaching the
                winning vote threshold or by an Admin decision.
              </span>
            </div>

            {closedMarkets.length === 0 ? (
              <div className="no-markets-message">
                <p>No resolved proposals found.</p>
              </div>
            ) : (
              <div className="resolution-markets-list">
                {closedMarkets.map((market) => {
                  const proposal = market.proposal!;
                  const winningOutcome = market.outcomes?.find(
                    (o) => o.outcome_id === proposal.winning_outcome_id,
                  );

                  return (
                    <div
                      key={market.id}
                      className="resolution-market-list-item"
                      onClick={() => onSelectMarket(market.id, "closed")}
                    >
                      <div className="list-item-main">
                        <div className="list-item-header">
                          <span className="market-type-badge">
                            {market.category}
                          </span>
                          <span className="status-badge resolved">
                            Resolved
                          </span>
                          {proposal.decided_by_admin && (
                            <span className="status-badge admin-resolved">
                              Admin
                            </span>
                          )}
                        </div>
                        <h3 className="list-item-question">
                          {market.question}
                        </h3>
                        <div className="list-item-meta">
                          <span className="meta-item">
                            <BadgeCheck {...metaIconProps} />
                            Proposal #{proposal.proposal_id}
                          </span>
                          <span className="meta-item">
                            <BadgeDollarSign {...metaIconProps} />
                            {formatVolume(market)}
                          </span>
                          <span className="meta-item">
                            <Clock3 {...metaIconProps} />
                            Expired: {formatDate(market.expireSec)}
                          </span>
                        </div>
                        <div className="resolution-proposal-info">
                          <div className="proposal-proposer">
                            Proposed by: {proposal.proposer}
                          </div>
                        </div>
                        {winningOutcome && (
                          <div className="resolved-outcome-label">
                            <CircleCheckBig {...outcomeIconProps} />
                            <span>
                              Winning Outcome:{" "}
                              <strong>{winningOutcome.name}</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        {activeSubTab === "illiquid" && (
          <>
            <h3 className="tab-page-title">Illiquid Markets</h3>
            <div className="resolution-info-banner warning">
             <TriangleAlert size={18} />
              <span>
                These expired markets are illiquid &mdash; they have either no
                trading activity or only one-sided trading. Resolution proposals
                cannot be created for these markets. An admin can mark them as
                refundable.
              </span>
            </div>

            {illiquidMarkets.length === 0 ? (
              <div className="no-markets-message">
                <p>No illiquid markets found.</p>
              </div>
            ) : (
              <div className="resolution-markets-list">
                {illiquidMarkets.map((market) => {
                  const illiquidType = getIlliquidType(market);
                  return (
                    <div
                      key={market.id}
                      className="resolution-market-list-item"
                      onClick={() => onSelectMarket(market.id, "illiquid")}
                    >
                      <div className="list-item-main">
                        <div className="list-item-header">
                          <span className="market-type-badge">
                            {market.category}
                          </span>
                          <span className="status-badge expired">Expired</span>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor:
                                illiquidType === "no_trading"
                                  ? "rgba(239, 68, 68, 0.2)"
                                  : "rgba(245, 158, 11, 0.2)",
                              color:
                                illiquidType === "no_trading"
                                  ? "#ef4444"
                                  : "#f59e0b",
                            }}
                          >
                            {illiquidType === "no_trading"
                              ? "No Trading"
                              : "One-Sided Trading"}
                          </span>
                        </div>
                        <h3 className="list-item-question">
                          {market.question}
                        </h3>
                        <div className="list-item-meta">
                          <span className="meta-item">
                            <BadgeDollarSign {...metaIconProps} />
                            {formatVolume(market)}
                          </span>
                          <span className="meta-item">
                            <Clock3 {...metaIconProps} />
                            Expired: {formatDate(market.expireSec)}
                          </span>
                          <span className="meta-item">
                            <List {...metaIconProps} />
                            {market.outcomes_count} outcomes
                          </span>
                        </div>
                        {market.outcomes && market.outcomes.length > 0 && (
                          <div className="resolution-outcomes-preview">
                            {market.outcomes.slice(0, 4).map((o) => (
                              <span key={o.outcome_id} className="outcome-tag">
                                {o.name}
                              </span>
                            ))}
                            {market.outcomes.length > 4 && (
                              <span className="outcome-tag more">
                                +{market.outcomes.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarketResolution;
